import { motion } from "framer-motion";
import { Loader2, MessageCircle } from "lucide-react";
import { useLanguage } from "@/i18n/LanguageProvider";

/**
 * O convite de WhatsApp, INLINE no fio da conversa.
 *
 * Não é bottom-sheet, e a diferença não é estética. O slot modal do primeiro
 * momento do Espelho já é do push soft-ask — que converteu 439 usuários e
 * quadruplicou o D1. Dois sheets no mesmo minuto matam os dois pedidos. Um
 * chip inline não disputa z-index com nada: ele é mais uma linha do fio, o
 * usuário rola por cima se não quiser, e o Espelho continua sendo quem fala.
 *
 * Custo aceito e registrado: chip converte menos que folha. Mas folha aqui não
 * está disponível — e chip que aparece vale mais que folha que não pode
 * aparecer.
 *
 * Visualmente segue o idioma do chip de ação do Espelho (card escuro, borda
 * ciano tênue, dois botões empilhados), sem herdar o MirrorActionChip: aquele
 * é dirigido por `proposal.tool` e assume escrita em tabela — este não escreve
 * linha de domínio nenhuma.
 */

interface WaSoftAskChipProps {
  onAccept: () => void;
  onDecline: () => void;
  activating: boolean;
  /** O último accept falhou — mostra waSoftAsk.error inline, sem matar o chip. */
  failed?: boolean;
}

export default function WaSoftAskChip({ onAccept, onDecline, activating, failed }: WaSoftAskChipProps) {
  const { t } = useLanguage();

  return (
    <motion.div
      initial={{ opacity: 0, y: 6 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.25 }}
      className="max-w-[86%] rounded-2xl rounded-bl-sm p-3.5 mt-1.5"
      style={{
        background: "hsl(222 18% 11%)",
        border: "1px solid hsl(187 72% 50% / 0.22)",
      }}
    >
      <div className="flex items-start gap-2.5 mb-2.5">
        <div
          className="w-8 h-8 rounded-xl flex items-center justify-center shrink-0"
          style={{
            background: "hsl(142 60% 45% / 0.14)",
            border: "1px solid hsl(142 60% 45% / 0.28)",
          }}
        >
          <MessageCircle size={15} style={{ color: "hsl(142 60% 58%)" }} aria-hidden="true" />
        </div>
        <div className="min-w-0 flex-1">
          <p className="text-[13px] font-display font-medium text-foreground/90 leading-snug">
            {t("waSoftAsk.title")}
          </p>
          <p className="text-[11px] font-body text-foreground/55 leading-relaxed mt-0.5">
            {t("waSoftAsk.body")}
          </p>
        </div>
      </div>
      <div className="flex flex-col gap-1.5">
        <button
          type="button"
          onClick={onAccept}
          disabled={activating}
          className="w-full rounded-xl font-display font-semibold text-[13px] tracking-wide transition-all active:scale-[0.98] disabled:opacity-60 flex items-center justify-center gap-2"
          style={{ minHeight: 42, background: "hsl(142 60% 42%)", color: "white" }}
        >
          {activating && <Loader2 size={14} className="animate-spin" />}
          {activating ? t("waSoftAsk.opening") : t("waSoftAsk.accept")}
        </button>
        <button
          type="button"
          onClick={onDecline}
          disabled={activating}
          className="w-full rounded-xl font-display font-medium text-[12px] tracking-wide transition-all active:scale-[0.98] disabled:opacity-60"
          style={{
            minHeight: 38,
            background: "transparent",
            border: "1px solid hsl(222 18% 22%)",
            color: "hsl(218 14% 68%)",
          }}
        >
          {t("waSoftAsk.decline")}
        </button>
        {failed && (
          <p
            role="alert"
            className="text-[11px] font-body leading-relaxed mt-0.5"
            style={{ color: "hsl(0 72% 62%)" }}
          >
            {t("waSoftAsk.error")}
          </p>
        )}
      </div>
    </motion.div>
  );
}
